import {
	type EngineEvent,
	type MatchState,
	type Move,
	MoveSchema,
	parseHexId,
} from "@fightclaw/engine";
import type { EngineEventsEvent } from "@fightclaw/protocol";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UnitAnimState } from "@/components/arena/unit-token";
import { HEX_RADIUS, hexIdToPixel } from "@/lib/hex-geo";

export type EngineEventsEnvelope = EngineEventsEvent;

export type ArenaEffect = {
	id: string;
	type:
		| "move-from"
		| "move-to"
		| "attack-source"
		| "attack-target"
		| "recruit"
		| "fortify"
		| "pass"
		| "attack-tracer";
	hexId: string;
	targetHexId?: string;
};

export type DamageNumberEntry = {
	id: string;
	hexId: string;
	value: number;
};

export type HudFx = {
	passPulse: boolean;
};

type QueuedItem = {
	envelope: EngineEventsEnvelope;
	postState?: MatchState;
};

const MAX_QUEUE = 8;
const MIN_STEP_MS = 50;

const isValidHex = (id: unknown): id is string =>
	typeof id === "string" && id.length >= 2;

const isEngineEvent = (value: unknown): value is EngineEvent =>
	typeof value === "object" &&
	value !== null &&
	"type" in value &&
	typeof (value as { type?: unknown }).type === "string";

const inferSide = (events: EngineEvent[]): "A" | "B" | null => {
	for (const event of events) {
		switch (event.type) {
			case "turn_start":
			case "turn_end":
			case "recruit":
			case "move_unit":
			case "fortify":
			case "attack":
			case "reject":
				return event.player;
			case "control_update":
			case "game_end":
				continue;
		}
	}
	return null;
};

const strongholdForSide = (side: "A" | "B"): string =>
	side === "A" ? "B2" : "B20";

/** Compute odd-r hex distance between two hex IDs. */
function hexDist(a: string, b: string): number {
	const pa = parseHexId(a);
	const pb = parseHexId(b);
	// Convert odd-r offset to cube coords
	const toCube = (row: number, col: number) => {
		const x = col - (row - (row & 1)) / 2;
		const z = row;
		const y = -x - z;
		return { x, y, z };
	};
	const ca = toCube(pa.row, pa.col);
	const cb = toCube(pb.row, pb.col);
	return Math.max(
		Math.abs(ca.x - cb.x),
		Math.abs(ca.y - cb.y),
		Math.abs(ca.z - cb.z),
	);
}

let effectCounter = 0;
function nextEffectId(): string {
	return `fx-${++effectCounter}`;
}

export function useArenaAnimator(options?: {
	onApplyBaseState?: (state: MatchState) => void;
}) {
	const onApplyBaseStateRef = useRef(options?.onApplyBaseState);
	onApplyBaseStateRef.current = options?.onApplyBaseState;

	const [effects, setEffects] = useState<ArenaEffect[]>([]);
	const [unitAnimStates, setUnitAnimStates] = useState<
		Map<string, UnitAnimState>
	>(new Map());
	const [dyingUnitIds, setDyingUnitIds] = useState<Set<string>>(new Set());
	const [hudFx, setHudFx] = useState<HudFx>({ passPulse: false });
	const [damageNumbers, setDamageNumbers] = useState<DamageNumberEntry[]>([]);
	const [lungeTargets, setLungeTargets] = useState<
		Map<string, { x: number; y: number }>
	>(new Map());
	const [isAnimating, setIsAnimating] = useState(false);

	const queueRef = useRef<QueuedItem[]>([]);
	const runningRef = useRef(false);
	const runTokenRef = useRef(0);
	const lastSeenStateVersionRef = useRef<number>(-1);
	const timeoutsRef = useRef<Set<number>>(new Set());

	const delay = useCallback((ms: number) => {
		return new Promise<void>((resolve) => {
			const id = window.setTimeout(() => {
				timeoutsRef.current.delete(id);
				resolve();
			}, ms);
			timeoutsRef.current.add(id);
		});
	}, []);

	const clearAllTimeouts = useCallback(() => {
		for (const id of timeoutsRef.current) {
			clearTimeout(id);
		}
		timeoutsRef.current.clear();
	}, []);

	const scaleForQueue = useCallback((queuedCount: number) => {
		const speedScale = queuedCount >= 6 ? 0.4 : 1;
		return (ms: number) => Math.max(MIN_STEP_MS, Math.round(ms * speedScale));
	}, []);

	const setUnitAnim = useCallback((unitId: string, state: UnitAnimState) => {
		setUnitAnimStates((prev) => {
			const next = new Map(prev);
			next.set(unitId, state);
			return next;
		});
	}, []);

	const clearUnitAnim = useCallback((unitId: string) => {
		setUnitAnimStates((prev) => {
			const next = new Map(prev);
			next.delete(unitId);
			return next;
		});
	}, []);

	const animateEnvelope = useCallback(
		async (item: QueuedItem, token: number) => {
			const scale = scaleForQueue(queueRef.current.length);
			const envelope = item.envelope;
			const moveParse = MoveSchema.safeParse(envelope.payload.move);
			const engineEvents = Array.isArray(envelope.payload.engineEvents)
				? envelope.payload.engineEvents.filter(isEngineEvent)
				: null;
			if (!moveParse.success || !engineEvents) {
				console.warn("arena-animator: skipped invalid engine_events payload", {
					move: envelope.payload.move,
					engineEvents: envelope.payload.engineEvents,
				});
				return;
			}
			const move: Move = moveParse.data;

			// Collect dying unit IDs from attack events BEFORE applying state
			const dyingIds: string[] = [];

			if (move.action === "attack") {
				const ev = engineEvents.find((e) => e.type === "attack") as
					| Extract<EngineEvent, { type: "attack" }>
					| undefined;
				if (ev) {
					dyingIds.push(...ev.outcome.defenderCasualties);
					dyingIds.push(...ev.outcome.attackerCasualties);
				}
			}

			// Mark dying units before applying post-state so HexBoard can keep them visible
			if (dyingIds.length > 0) {
				setDyingUnitIds(new Set(dyingIds));
				for (const id of dyingIds) {
					setUnitAnim(id, "dying");
				}
			}

			// Apply post-state (dead units are removed from state here)
			if (item.postState && onApplyBaseStateRef.current) {
				onApplyBaseStateRef.current(item.postState);
			}

			// Clear previous effects
			setEffects([]);

			if (move.action === "move") {
				const ev = engineEvents.find((e) => e.type === "move_unit") as
					| Extract<EngineEvent, { type: "move_unit" }>
					| undefined;
				if (ev && isValidHex(ev.from) && isValidHex(ev.to)) {
					setUnitAnim(ev.unitId, "moving");
					setEffects([
						{ id: nextEffectId(), type: "move-from", hexId: ev.from },
						{ id: nextEffectId(), type: "move-to", hexId: ev.to },
					]);
					await delay(scale(350));
					if (runTokenRef.current !== token) return;

					clearUnitAnim(ev.unitId);
					setEffects([]);
					await delay(scale(120));
					if (runTokenRef.current !== token) return;
				}
			} else if (move.action === "attack") {
				const ev = engineEvents.find((e) => e.type === "attack") as
					| Extract<EngineEvent, { type: "attack" }>
					| undefined;
				if (ev && isValidHex(ev.attackerFrom) && isValidHex(ev.targetHex)) {
					setUnitAnim(ev.attackerId, "attacking");

					// Compute lunge target pixel coords
					const targetPixel = hexIdToPixel(ev.targetHex, HEX_RADIUS);
					setLungeTargets(
						new Map([[ev.attackerId, { x: targetPixel.x, y: targetPixel.y }]]),
					);

					const fxList: ArenaEffect[] = [
						{
							id: nextEffectId(),
							type: "attack-source",
							hexId: ev.attackerFrom,
						},
						{
							id: nextEffectId(),
							type: "attack-target",
							hexId: ev.targetHex,
						},
					];

					// Ranged attack tracer (distance > 1)
					const dist = hexDist(ev.attackerFrom, ev.targetHex);
					if (dist > 1) {
						fxList.push({
							id: nextEffectId(),
							type: "attack-tracer",
							hexId: ev.attackerFrom,
							targetHexId: ev.targetHex,
						});
					}

					setEffects(fxList);

					// Emit damage number
					const dmg = ev.outcome.damageDealt;
					if (dmg > 0) {
						setDamageNumbers([
							{ id: nextEffectId(), hexId: ev.targetHex, value: dmg },
						]);
					}

					await delay(scale(170));
					if (runTokenRef.current !== token) return;

					// Dying units are already marked; wait for exit animation
					await delay(scale(160));
					if (runTokenRef.current !== token) return;

					clearUnitAnim(ev.attackerId);
					for (const id of dyingIds) {
						clearUnitAnim(id);
					}
					setDyingUnitIds(new Set());
					setEffects([]);
					setDamageNumbers([]);
					setLungeTargets(new Map());
					await delay(scale(120));
					if (runTokenRef.current !== token) return;
				}
			} else if (move.action === "recruit") {
				const ev = engineEvents.find((e) => e.type === "recruit") as
					| Extract<EngineEvent, { type: "recruit" }>
					| undefined;
				if (ev && isValidHex(ev.at)) {
					setUnitAnim(ev.unitId, "spawning");
					setEffects([{ id: nextEffectId(), type: "recruit", hexId: ev.at }]);
					await delay(scale(320));
					if (runTokenRef.current !== token) return;

					clearUnitAnim(ev.unitId);
					setEffects([]);
					await delay(scale(120));
					if (runTokenRef.current !== token) return;
				}
			} else if (move.action === "fortify") {
				const ev = engineEvents.find((e) => e.type === "fortify") as
					| Extract<EngineEvent, { type: "fortify" }>
					| undefined;
				if (ev && isValidHex(ev.at)) {
					setUnitAnim(ev.unitId, "fortifying");
					setEffects([{ id: nextEffectId(), type: "fortify", hexId: ev.at }]);
					await delay(scale(300));
					if (runTokenRef.current !== token) return;

					clearUnitAnim(ev.unitId);
					setEffects([]);
				}
			} else if (move.action === "end_turn" || move.action === "pass") {
				const side = inferSide(engineEvents) ?? "A";
				const key = strongholdForSide(side);

				setHudFx((prev) => ({ ...prev, passPulse: true }));
				setEffects([{ id: nextEffectId(), type: "pass", hexId: key }]);
				await delay(scale(260));
				if (runTokenRef.current !== token) return;

				setHudFx((prev) => ({ ...prev, passPulse: false }));
				setEffects([]);
				await delay(scale(120));
				if (runTokenRef.current !== token) return;
			}
		},
		[clearUnitAnim, delay, scaleForQueue, setUnitAnim],
	);

	const runLoop = useCallback(async () => {
		const token = runTokenRef.current;
		for (;;) {
			if (!runningRef.current) break;
			const next = queueRef.current.shift() ?? null;
			if (!next) {
				runningRef.current = false;
				setIsAnimating(false);
				break;
			}

			await animateEnvelope(next, token);
			if (runTokenRef.current !== token) break;
		}
	}, [animateEnvelope]);

	const enqueue = useCallback(
		(envelope: EngineEventsEnvelope, opts?: { postState?: MatchState }) => {
			if (
				typeof envelope.stateVersion === "number" &&
				envelope.stateVersion <= lastSeenStateVersionRef.current
			) {
				return;
			}
			if (typeof envelope.stateVersion === "number") {
				lastSeenStateVersionRef.current = envelope.stateVersion;
			}

			const queue = queueRef.current;
			if (queue.length >= MAX_QUEUE) {
				queue.splice(0, queue.length - MAX_QUEUE + 1);
			}
			queue.push({ envelope, postState: opts?.postState });

			if (!runningRef.current) {
				runningRef.current = true;
				setIsAnimating(true);
				void runLoop();
			}
		},
		[runLoop],
	);

	const reset = useCallback(() => {
		queueRef.current = [];
		runningRef.current = false;
		runTokenRef.current += 1;
		lastSeenStateVersionRef.current = -1;
		setEffects([]);
		setUnitAnimStates(new Map());
		setDyingUnitIds(new Set());
		setHudFx({ passPulse: false });
		setDamageNumbers([]);
		setLungeTargets(new Map());
		setIsAnimating(false);
		clearAllTimeouts();
	}, [clearAllTimeouts]);

	useEffect(() => {
		return () => {
			reset();
		};
	}, [reset]);

	const api = useMemo(
		() => ({
			effects,
			unitAnimStates,
			dyingUnitIds,
			hudFx,
			damageNumbers,
			lungeTargets,
			isAnimating,
			enqueue,
			reset,
		}),
		[
			effects,
			unitAnimStates,
			dyingUnitIds,
			hudFx,
			damageNumbers,
			lungeTargets,
			isAnimating,
			enqueue,
			reset,
		],
	);

	return api;
}
